import {useSelector} from 'react-redux';
import {Button, Col, Row, Slider, Space} from 'antd';
import "./Player.css"

import {PauseCircleOutlined, PlayCircleOutlined, CloseOutlined, DownloadOutlined} from "@ant-design/icons";
import React, {useEffect, useRef, useState, useCallback} from "react";
import WaveSurfer from "wavesurfer.js";


export const Player = () => {
    const wsRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [showPlayer, setShowPlayer] = useState(false);
    const currentRecordingIdRef = useRef(null);

    const recording = useSelector((state) => state.recordings.currentRecording);
    const recordingUpdatedAt = useSelector((state) => state.recordings.currentRecordingUpdatedAt);
    const containerRef = useRef(null);

    // Initialize WaveSurfer once
    useEffect(() => {
        if (!containerRef.current || wsRef.current) return;

        const ws = WaveSurfer.create({
            container: containerRef.current,
            waveColor: '#667eea',
            progressColor: '#764ba2',
            height: 40,
            normalize: true,
            barWidth: 2,
            barGap: 1,
            barRadius: 2,
        });

        ws.on('ready', () => {
            setDuration(ws.getDuration());
            ws.play();
            setIsPlaying(true);
        });

        ws.on('play', () => setIsPlaying(true));
        ws.on('pause', () => setIsPlaying(false));
        ws.on('finish', () => setIsPlaying(false));
        ws.on('timeupdate', () => setCurrentTime(ws.getCurrentTime()));

        wsRef.current = ws;

        return () => {
            if (wsRef.current) {
                wsRef.current.destroy();
                wsRef.current = null;
            }
        };
    }, []);

    // Load recording when selected
    useEffect(() => {
        if (!wsRef.current || !recording) {
            setShowPlayer(false);
            currentRecordingIdRef.current = null;
            return;
        }

        // If it's the same recording that was playing (but minimized), just show the player
        if (currentRecordingIdRef.current === recording.id) {
            setShowPlayer(true);
            return;
        }

        // Different recording - load it
        currentRecordingIdRef.current = recording.id;
        setShowPlayer(true);
        setCurrentTime(0);
        wsRef.current.load(recording.mp3_file_uri);
    }, [recording, recordingUpdatedAt]);

    const handlePlayPause = useCallback(() => {
        if (!wsRef.current) return;
        wsRef.current.isPlaying() ? wsRef.current.pause() : wsRef.current.play();
    }, []);

    const handleClose = useCallback(() => {
        // Hide the player but keep audio playing in background
        setShowPlayer(false);
    }, []);

    const handleSeek = useCallback((value) => {
        if (wsRef.current) {
            wsRef.current.seekTo(value / 100);
            setCurrentTime(wsRef.current.getDuration() * (value / 100));
        }
    }, []);

    const formatTime = (seconds) => {
        if (!seconds) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="player-wrapper" style={{ display: showPlayer ? 'block' : 'none' }}>
            {/* Track Info */}
            <div className="player-track-info">
                <div className="player-title">{recording?.original_name}</div>
                {recording?.labels && recording.labels.length > 0 && (
                    <div className="player-labels">
                        {recording.labels.map((label, idx) => (
                            <span key={idx} className="player-label-tag">{label.label_name}</span>
                        ))}
                    </div>
                )}
            </div>

            {/* Waveform and Controls */}
            <Row gutter={[8, 0]} align="middle" style={{width: '100%'}}>
                <Col xs={2} sm={1} style={{display: 'flex', justifyContent: 'center'}}>
                    <Button
                        type="text"
                        onClick={handlePlayPause}
                        icon={isPlaying ? <PauseCircleOutlined style={{fontSize: '18px'}} /> : <PlayCircleOutlined style={{fontSize: '18px'}} />}
                        style={{border: 'none', padding: '4px'}}
                    />
                </Col>
                <Col xs={18} sm={20} style={{minHeight: '40px'}}>
                    <div ref={containerRef} style={{width: '100%'}} />
                </Col>
                <Col xs={2} sm={1} style={{display: 'flex', justifyContent: 'center'}}>
                    <a href={recording?.file_uri} download>
                        <Button
                            type="text"
                            icon={<DownloadOutlined style={{fontSize: '16px'}} />}
                            style={{border: 'none', padding: '4px'}}
                        />
                    </a>
                </Col>
                <Col xs={2} sm={1} style={{display: 'flex', justifyContent: 'center'}}>
                    <Button
                        type="text"
                        onClick={handleClose}
                        icon={<CloseOutlined style={{fontSize: '16px'}} />}
                        style={{border: 'none', padding: '4px'}}
                    />
                </Col>
            </Row>

            {/* Time and Seek */}
            <div className="player-timeline">
                <span className="player-time">{formatTime(currentTime)}</span>
                <Slider
                    value={duration > 0 ? (currentTime / duration) * 100 : 0}
                    onChange={handleSeek}
                    style={{flex: 1, margin: '0 8px'}}
                    tooltip={{formatter: null}}
                />
                <span className="player-time">{formatTime(duration)}</span>
            </div>
        </div>
    );
};
